import { useRef, useState } from "react";
import { Video, Camera, Loader2, X, RotateCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { VideoItem } from "@/components/rastreo/VideoItem";
import { subirVideoEnlace, MAX_VIDEO_BYTES, EXT_VIDEO } from "@/lib/api";
import { RASTREO } from "@/constants/testIds";

const ACCEPT = "video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm";

const mensajeFallo = (f) => {
  if (f.status === 0) return "Se perdió la conexión. Revisa tu internet y reintenta: no se duplicará el video.";
  if (f.status === 413) return "El video supera el máximo de 50 MB.";
  if (f.status === 429) return "Demasiados intentos seguidos. Espera un minuto y reintenta.";
  return f.detalle || "No se pudo enviar el video. Reintenta en un momento.";
};

// Panel que solo aparece cuando la página se abrió con un enlace de unidad válido.
export const VideosEnlace = ({ token, info, onChange }) => {
  const [estado, setEstado] = useState("reposo"); // reposo | subiendo | error | ok
  const [progreso, setProgreso] = useState(0);
  const [error, setError] = useState("");
  const pendiente = useRef(null); // { archivo, clave } se conserva para reintentar sin duplicar
  const cancelarRef = useRef(null);
  const enCurso = useRef(false);
  const inputGaleria = useRef(null);
  const inputCamara = useRef(null);

  const { videos, max_videos: max, puede_subir: puedeSubir } = info;

  const enviar = async () => {
    if (enCurso.current || !pendiente.current) return;
    enCurso.current = true;
    setEstado("subiendo");
    setProgreso(0);
    setError("");
    const { archivo, clave } = pendiente.current;
    const { promesa, cancelar } = subirVideoEnlace(token, archivo, clave, setProgreso);
    cancelarRef.current = cancelar;
    try {
      await promesa;
      pendiente.current = null;
      setEstado("ok");
      await onChange();
    } catch (f) {
      if (f.status === -1) {
        pendiente.current = null;
        setEstado("reposo");
      } else {
        setError(mensajeFallo(f));
        setEstado("error");
      }
    } finally {
      enCurso.current = false;
      cancelarRef.current = null;
    }
  };

  const elegir = (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo || enCurso.current) return;
    const ext = archivo.name.split(".").pop().toLowerCase();
    if (!EXT_VIDEO.includes(ext)) {
      setError("Formato no permitido. Usa MP4, MOV o WebM.");
      setEstado("error");
      pendiente.current = null;
      return;
    }
    if (archivo.size > MAX_VIDEO_BYTES) {
      setError("El video supera el máximo de 50 MB. Graba uno más corto o en menor calidad.");
      setEstado("error");
      pendiente.current = null;
      return;
    }
    pendiente.current = { archivo, clave: crypto.randomUUID() };
    enviar();
  };

  const subiendo = estado === "subiendo";

  return (
    <section
      data-testid={RASTREO.videosPanel}
      className="mt-5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6 tc-fade-up"
    >
      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">
        Videos de tu vehículo ({videos.length}/{max})
      </h2>
      <p className="mt-2 text-sm text-[var(--tc-text-secondary)]">
        Puedes enviarnos un video del estado de tu vehículo. MP4, MOV o WebM, hasta 50 MB, un archivo por envío.
      </p>

      <input ref={inputGaleria} data-testid={RASTREO.videoFileInput} type="file" accept={ACCEPT} className="hidden" onChange={elegir} />
      <input ref={inputCamara} data-testid={RASTREO.videoCameraInput} type="file" accept="video/*" capture="environment" className="hidden" onChange={elegir} />

      {puedeSubir ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            data-testid={RASTREO.videoAddButton}
            disabled={subiendo}
            onClick={() => inputGaleria.current?.click()}
            className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:opacity-50"
          >
            <Video size={16} /> Añadir video
          </button>
          <button
            data-testid={RASTREO.videoCameraButton}
            disabled={subiendo}
            onClick={() => inputCamara.current?.click()}
            className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-4 py-2.5 text-sm font-medium text-[var(--tc-text)] transition-colors hover:border-[var(--tc-accent)] disabled:opacity-50"
          >
            <Camera size={16} /> Grabar con la cámara
          </button>
        </div>
      ) : (
        <p className="mt-4 text-sm text-[var(--tc-text-secondary)]">Ya se alcanzó el máximo de {max} videos para esta unidad.</p>
      )}

      {subiendo && (
        <div data-testid={RASTREO.videoProgress} className="mt-4" role="status">
          <div className="flex items-center justify-between text-sm text-[var(--tc-text-secondary)]">
            <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Enviando… {progreso}%</span>
            <button
              data-testid={RASTREO.videoCancelButton}
              onClick={() => cancelarRef.current?.()}
              className="inline-flex items-center gap-1 font-medium text-red-600 hover:underline"
            >
              <X size={14} /> Cancelar
            </button>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--tc-border)]">
            <div className="h-full bg-[var(--tc-accent)] transition-[width]" style={{ width: `${progreso}%` }} />
          </div>
        </div>
      )}

      {estado === "error" && (
        <div data-testid={RASTREO.videoError} role="alert" className="mt-4 rounded-[var(--tc-radius)] border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p className="flex items-start gap-2"><AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}</p>
          {pendiente.current && (
            <button
              data-testid={RASTREO.videoRetryButton}
              onClick={enviar}
              className="mt-2 inline-flex items-center gap-1.5 font-semibold hover:underline"
            >
              <RotateCw size={14} /> Reintentar
            </button>
          )}
        </div>
      )}

      {estado === "ok" && (
        <p data-testid={RASTREO.videoSuccess} role="status" className="mt-4 flex items-center gap-2 text-sm font-medium text-emerald-700">
          <CheckCircle2 size={16} /> Video guardado correctamente.
        </p>
      )}

      {videos.length > 0 && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {videos.map((v) => <VideoItem key={v.id} video={v} />)}
        </div>
      )}
    </section>
  );
};
